/**
 * useTodos — live cross-tool "to-do" aggregator for the notification bell.
 *
 * Each tool contributes the items currently awaiting the user's action,
 * derived from LIVE queries gated by the user's approver access. Because they
 * come from live state, an item disappears (and the count drops) the moment
 * anyone actions the underlying doc — no separate notification store to keep
 * in sync.
 *
 * Deep links go to the exact item where possible; Absence links to its queue
 * (it has no per-item detail page).
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useProfile } from '../profile/ProfileProvider';
import { useIsInvoiceApprover } from '../invoices/hooks/useIsInvoiceApprover';
import { useIsCreditCardApprover } from '../creditCards/hooks/useIsCreditCardApprover';
import type {
  Statement,
  InvoiceSubmission,
  CreditCardStatement,
  CreditCardReceiptRequest,
  AbsenceRequest,
  Feedback,
  TimeStatement,
  ReviewAssignment,
  OnboardingChecklist,
  SignatureRequest,
  ReviewNominationTasksResponse,
  AbsencePendingApprovalsResponse,
  BqStatusAlert,
  ProjectActionItem,
  DirectorInputTodo,
} from '@daaily/shared';
import { CALLABLES, COLLECTIONS, formatCurrency, periodLabel, isSignersTurn, isAnalyticsAdmin, isSickCertOutstanding } from '@daaily/shared';

const callNominations = httpsCallable<unknown, ReviewNominationTasksResponse>(functions, CALLABLES.REVIEW_NOMINATION_TASKS);
const callPendingAbsence = httpsCallable<unknown, AbsencePendingApprovalsResponse>(functions, CALLABLES.ABSENCE_PENDING_APPROVALS);
const callDirectorInputs = httpsCallable<unknown, { todos: DirectorInputTodo[] }>(functions, CALLABLES.DIRECTORS_MY_INPUTS);

export type TodoKind = 'absence' | 'expense' | 'invoice' | 'creditcard' | 'review' | 'onboarding' | 'signature' | 'analytics' | 'projects' | 'directors';

export interface Todo {
  id: string;
  kind: TodoKind;
  tool: string;
  label: string;
  link: string;
  /** Optional finer grouping within a tool (e.g. Onboarding vs Offboarding). */
  subgroup?: string;
  /**
   * Set on TEAM-side (approval-queue) items only — the sidebar route of the
   * tool whose "Team" sub-nav entry should carry this item in its count badge
   * (e.g. '/expenses'). Own/personal to-dos leave it unset.
   */
  teamRoute?: string;
}

export function useTodos(): Todo[] {
  const { state } = useProfile();
  const isInvoiceApprover = useIsInvoiceApprover();
  const isCreditCardApprover = useIsCreditCardApprover();

  const ready = state.status === 'ready';
  const email = ready ? state.user.email.toLowerCase() : null;
  const isAccountingOrAdmin = ready && (state.isAccounting || state.isAdmin);
  const absenceSeeAll =
    ready &&
    (state.isAdmin ||
      (state.config.hubAdminEmails ?? []).map((e) => e.toLowerCase()).includes(email ?? ''));

  const [expense, setExpense] = useState<Todo[]>([]);
  const [invoice, setInvoice] = useState<Todo[]>([]);
  const [cc, setCc] = useState<Todo[]>([]);
  const [absence, setAbsence] = useState<Todo[]>([]);
  // "Own" to-dos — the caller's own items needing action.
  const [ownExpense, setOwnExpense] = useState<Todo[]>([]);
  const [ownInvoice, setOwnInvoice] = useState<Todo[]>([]);
  const [ownCc, setOwnCc] = useState<Todo[]>([]);
  // Receipt requests assigned to me (provide-a-receipt task) — any user.
  const [receiptReq, setReceiptReq] = useState<Todo[]>([]);
  // Documents waiting for my signature — any user.
  const [signatures, setSignatures] = useState<Todo[]>([]);
  // My feedback that was resolved (completed/rejected) and not yet acknowledged.
  const [feedback, setFeedback] = useState<Todo[]>([]);
  // Time sheets awaiting my approval + my own rejected time sheets.
  const [timeApprovals, setTimeApprovals] = useState<Todo[]>([]);
  const [ownTime, setOwnTime] = useState<Todo[]>([]);
  const [reviews, setReviews] = useState<Todo[]>([]);
  const [nominations, setNominations] = useState<Todo[]>([]);
  const [onboarding, setOnboarding] = useState<Todo[]>([]);
  const [projectItems, setProjectItems] = useState<Todo[]>([]);
  // Directors' Room topics where someone tagged me with @ (callable — the
  // director collections are server-only). Refetched on focus + every 5 min.
  const [directorInputs, setDirectorInputs] = useState<Todo[]>([]);
  // New-BigQuery-table alerts (analytics admins only).
  const [bqAlerts, setBqAlerts] = useState<Todo[]>([]);
  // Chain-up absence approvals (requester deeper in my reporting line) — the
  // live query above only covers approverEmail == me; rules can't traverse the
  // manager tree, so these come from the callable (one fetch per session, like
  // nominations). seeAll callers already get everything from the live query.
  const [absenceChain, setAbsenceChain] = useState<Todo[]>([]);
  // My own sick leaves whose medical certificate I still owe (upload-later mode).
  const [ownCert, setOwnCert] = useState<Todo[]>([]);

  // Expense statements awaiting approval.
  useEffect(() => {
    if (!isAccountingOrAdmin) {
      setExpense([]);
      return;
    }
    const q = query(
      collection(db, 'statements'),
      where('status', 'in', ['submitted', 'pending_2nd_approval']),
    );
    return onSnapshot(
      q,
      (snap) =>
        setExpense(
          snap.docs.map((d) => {
            const s = d.data() as Statement;
            return {
              id: `exp:${d.id}`,
              kind: 'expense' as const,
              tool: 'Expenses',
              label: `Expense statement — ${s.ownerDisplayName || s.ownerEmail}`,
              link: `/expenses/statements/${d.id}`,
              teamRoute: '/expenses',
            };
          }),
        ),
      () => setExpense([]),
    );
  }, [isAccountingOrAdmin]);

  // Invoices awaiting approval.
  useEffect(() => {
    if (!isInvoiceApprover) {
      setInvoice([]);
      return;
    }
    const q = query(collection(db, 'invoiceSubmissions'), where('status', '==', 'submitted'));
    return onSnapshot(
      q,
      (snap) =>
        setInvoice(
          snap.docs.map((d) => {
            const v = d.data() as InvoiceSubmission;
            return {
              id: `inv:${d.id}`,
              kind: 'invoice' as const,
              tool: 'Invoices',
              label: `Invoice — ${v.submitterDisplayName || v.submitterEmail}`,
              link: `/invoices/approvals/${d.id}`,
              teamRoute: '/invoices',
            };
          }),
        ),
      () => setInvoice([]),
    );
  }, [isInvoiceApprover]);

  // Credit-card statements awaiting approval.
  useEffect(() => {
    if (!isCreditCardApprover) {
      setCc([]);
      return;
    }
    const q = query(collection(db, 'creditCardStatements'), where('status', '==', 'submitted'));
    return onSnapshot(
      q,
      (snap) =>
        setCc(
          snap.docs.map((d) => {
            const s = d.data() as CreditCardStatement;
            return {
              id: `cc:${d.id}`,
              kind: 'creditcard' as const,
              tool: 'Credit Cards',
              label: `Credit card — ${s.ownerDisplayName || s.ownerEmail} · ${s.periodLabel}`,
              link: `/credit-cards/statements/${d.id}`,
              teamRoute: '/credit-cards',
            };
          }),
        ),
      () => setCc([]),
    );
  }, [isCreditCardApprover]);

  // Absence requests awaiting approval.
  useEffect(() => {
    if (!email) {
      setAbsence([]);
      return;
    }
    const col = collection(db, 'absenceRequests');
    const q = absenceSeeAll
      ? query(col, where('status', '==', 'submitted'))
      : query(col, where('approverEmail', '==', email));
    return onSnapshot(
      q,
      (snap) => {
        let rows = snap.docs.map((d) => ({ id: d.id, r: d.data() as AbsenceRequest }));
        if (!absenceSeeAll) rows = rows.filter((x) => x.r.status === 'submitted');
        setAbsence(
          rows.map(({ id, r }) => ({
            id: `abs:${id}`,
            kind: 'absence' as const,
            tool: 'Absence',
            label: `Absence — ${r.ownerDisplayName} · ${r.type}`,
            link: '/absence/approvals',
            teamRoute: '/absence',
          })),
        );
      },
      () => setAbsence([]),
    );
  }, [email, absenceSeeAll]);

  // Own outstanding medical certificates → a to-do that links to the upload box on the dashboard.
  useEffect(() => {
    if (!email) {
      setOwnCert([]);
      return;
    }
    const q = query(collection(db, 'absenceRequests'), where('ownerEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setOwnCert(
          snap.docs
            .map((d) => ({ id: d.id, r: d.data() as AbsenceRequest }))
            .filter((x) => isSickCertOutstanding(x.r))
            .map(({ id, r }) => ({
              id: `cert:${id}`,
              kind: 'absence' as const,
              tool: 'Absence',
              label: `Attach the medical certificate for your sick leave (${r.startDate === r.endDate ? r.startDate : `${r.startDate} → ${r.endDate}`})`,
              link: '/absence',
            })),
        ),
      () => setOwnCert([]),
    );
  }, [email]);

  // Own rejected expense statements (single-field query; filter client-side → no index).
  useEffect(() => {
    if (!email) {
      setOwnExpense([]);
      return;
    }
    const q = query(collection(db, 'statements'), where('ownerEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setOwnExpense(
          snap.docs
            .map((d) => ({ id: d.id, s: d.data() as Statement }))
            .filter((x) => x.s.status === 'rejected')
            .map(({ id }) => ({
              id: `own-exp:${id}`,
              kind: 'expense' as const,
              tool: 'Expenses',
              label: 'Rejected expense statement — fix & resubmit',
              link: `/expenses/statements/${id}`,
            })),
        ),
      () => setOwnExpense([]),
    );
  }, [email]);

  // Own rejected invoices.
  useEffect(() => {
    if (!email) {
      setOwnInvoice([]);
      return;
    }
    const q = query(collection(db, 'invoiceSubmissions'), where('submitterEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setOwnInvoice(
          snap.docs
            .map((d) => ({ id: d.id, v: d.data() as InvoiceSubmission }))
            .filter((x) => x.v.status === 'rejected')
            .map(({ id }) => ({
              id: `own-inv:${id}`,
              kind: 'invoice' as const,
              tool: 'Invoices',
              label: 'Rejected invoice — fix & resubmit',
              link: `/invoices/${id}`,
            })),
        ),
      () => setOwnInvoice([]),
    );
  }, [email]);

  // Own credit-card statements: rejected (resubmit) or assigned-and-not-yet-submitted (draft + deadline).
  useEffect(() => {
    if (!email) {
      setOwnCc([]);
      return;
    }
    const q = query(collection(db, 'creditCardStatements'), where('ownerEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setOwnCc(
          snap.docs.flatMap((d) => {
            const s = d.data() as CreditCardStatement;
            if (s.status === 'rejected') {
              return [{
                id: `own-cc:${d.id}`,
                kind: 'creditcard' as const,
                tool: 'Credit Cards',
                label: 'Rejected credit-card statement — fix & resubmit',
                link: `/credit-cards/statements/${d.id}`,
              }];
            }
            if (s.status === 'draft' && s.submissionDeadline) {
              return [{
                id: `own-cc:${d.id}`,
                kind: 'creditcard' as const,
                tool: 'Credit Cards',
                label: 'Submit your assigned credit-card statement',
                link: `/credit-cards/statements/${d.id}`,
              }];
            }
            return [];
          }),
        ),
      () => setOwnCc([]),
    );
  }, [email]);

  // Receipt requests assigned to me — open ones I need to fulfil (any user).
  useEffect(() => {
    if (!email) {
      setReceiptReq([]);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.CREDIT_CARD_RECEIPT_REQUESTS),
      where('requestedEmail', '==', email),
    );
    return onSnapshot(
      q,
      (snap) =>
        setReceiptReq(
          snap.docs
            .map((d) => d.data() as CreditCardReceiptRequest)
            .filter((r) => r.status === 'open')
            .map((r) => ({
              id: `rcptreq:${r.id}`,
              kind: 'creditcard' as const,
              tool: 'Credit Cards',
              label: `Provide a receipt — ${r.merchant} · ${formatCurrency(r.amount, r.currency)}`,
              link: `/receipt-request/${r.id}`,
            })),
        ),
      () => setReceiptReq([]),
    );
  }, [email]);

  // Documents waiting for MY signature (any user — no tool access needed).
  useEffect(() => {
    if (!email) {
      setSignatures([]);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.SIGNATURE_REQUESTS),
      where('signerEmails', 'array-contains', email),
    );
    return onSnapshot(
      q,
      (snap) =>
        setSignatures(
          snap.docs
            .map((d) => d.data() as SignatureRequest)
            // Ordered requests only surface when it's actually MY turn.
            .filter((r) => r.status === 'pending' && isSignersTurn(r, email))
            .map((r) => ({
              id: `sig:${r.id}`,
              kind: 'signature' as const,
              tool: 'Signatures',
              label: `Sign "${r.title}" — from ${r.createdByDisplayName}`,
              link: `/sign/${r.id}`,
              // Badge on the Signatures nav item (it's a personal action, so the
              // "Personal" child carries the count — see Layout).
              teamRoute: '/signatures',
            })),
        ),
      () => setSignatures([]),
    );
  }, [email]);

  // My feedback resolved (completed/rejected) but not yet acknowledged. on_hold
  // and open produce NO notification.
  useEffect(() => {
    if (!email) {
      setFeedback([]);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.FEEDBACK),
      where('submitterEmail', '==', email),
    );
    return onSnapshot(
      q,
      (snap) =>
        setFeedback(
          snap.docs
            .map((d) => d.data() as Feedback)
            .filter(
              (f) => (f.status === 'completed' || f.status === 'rejected') && !f.acknowledgedAt,
            )
            .map((f) => ({
              id: `fb:${f.id}`,
              kind: 'creditcard' as const,
              tool: 'Feedback',
              label:
                f.status === 'completed'
                  ? `Your feedback was completed — ${f.text.slice(0, 40)}`
                  : `Your feedback was rejected${f.resolutionReason ? `: ${f.resolutionReason.slice(0, 50)}` : ''}`,
              link: '/feedback',
            })),
        ),
      () => setFeedback([]),
    );
  }, [email]);

  // Time sheets awaiting my approval (approverEmail == me, submitted).
  useEffect(() => {
    if (!email) {
      setTimeApprovals([]);
      return;
    }
    const q = query(collection(db, COLLECTIONS.TIME_STATEMENTS), where('approverEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setTimeApprovals(
          snap.docs
            .map((d) => d.data() as TimeStatement)
            .filter((s) => s.status === 'submitted')
            .map((s) => ({
              id: `timeappr:${s.id}`,
              kind: 'absence' as const,
              tool: 'Time Tracking',
              label: `Time sheet — ${s.userDisplayName} · ${periodLabel(s.period)}`,
              link: `/time?review=${s.id}`,
              teamRoute: '/time',
            })),
        ),
      () => setTimeApprovals([]),
    );
  }, [email]);

  // My own time sheets that were sent back (rejected) — adjust + resubmit.
  useEffect(() => {
    if (!email) {
      setOwnTime([]);
      return;
    }
    const q = query(collection(db, COLLECTIONS.TIME_STATEMENTS), where('userEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setOwnTime(
          snap.docs
            .map((d) => d.data() as TimeStatement)
            .filter((s) => s.status === 'rejected')
            .map((s) => ({
              id: `owntime:${s.id}`,
              kind: 'absence' as const,
              tool: 'Time Tracking',
              label: `Time sheet sent back — ${periodLabel(s.period)} (adjust & resubmit)`,
              link: '/time',
            })),
        ),
      () => setOwnTime([]),
    );
  }, [email]);

  // Performance reviews assigned to me and not yet submitted (self/manager/peer).
  useEffect(() => {
    if (!email) {
      setReviews([]);
      return;
    }
    const q = query(collection(db, 'reviewAssignments'), where('reviewerEmail', '==', email));
    return onSnapshot(
      q,
      (snap) =>
        setReviews(
          snap.docs
            .map((d) => d.data() as ReviewAssignment)
            .filter((a) => a.status === 'assigned' || a.status === 'in_progress')
            .map((a) => ({
              id: `review:${a.id}`,
              kind: 'review' as const,
              tool: 'Performance Reviews',
              label: (a.relationship ?? 'self') === 'self'
                ? (a.surveyName || 'Complete your self-review')
                : (a.relationship === 'company' ? (a.surveyName || 'Company survey') : `${a.surveyName || 'Review'}: ${a.subjectDisplayName}`),
              link: `/performance-reviews/${encodeURIComponent(a.id)}`,
            })),
        ),
      () => setReviews([]),
    );
  }, [email]);

  // Chain-up absence approvals via the callable (non-seeAll managers only).
  useEffect(() => {
    if (!email || absenceSeeAll) { setAbsenceChain([]); return; }
    let alive = true;
    void (async () => {
      try {
        const res = await callPendingAbsence({});
        if (!alive) return;
        setAbsenceChain(
          res.data.requests
            .filter((r) => r.bucket === 'chain')
            .map((r) => ({
              id: `abs:${r.id}`,
              kind: 'absence' as const,
              tool: 'Absence',
              label: `Absence — ${r.ownerDisplayName} · ${r.type}`,
              link: '/absence/approvals',
              teamRoute: '/absence',
            })),
        );
      } catch { if (alive) setAbsenceChain([]); }
    })();
    return () => { alive = false; };
  }, [email, absenceSeeAll]);

  // Peer-nomination duties: subjects I lead in a cycle whose nomination is open
  // (callable-driven — the org snapshot isn't client-readable). One fetch per session.
  useEffect(() => {
    if (!email) { setNominations([]); return; }
    let alive = true;
    void (async () => {
      try {
        const tasks = (await callNominations({})).data.tasks;
        if (!alive) return;
        setNominations(
          tasks.filter((t) => t.peerCount === 0).map((t) => ({
            id: `review-nom:${t.cycleId}:${t.subjectEmail}`,
            kind: 'review' as const,
            tool: 'Performance Reviews',
            label: `Nominate peer reviewers for ${t.subjectDisplayName}`,
            link: `/performance-reviews/cycle/${t.cycleId}?tab=team`,
          })),
        );
      } catch { if (alive) setNominations([]); }
    })();
    return () => { alive = false; };
  }, [email]);

  // Onboarding checklists where I'm responsible + still have open tasks.
  useEffect(() => {
    if (!email) {
      setOnboarding([]);
      return;
    }
    const q = query(collection(db, COLLECTIONS.ONBOARDING_CHECKLISTS), where('responsibleEmails', 'array-contains', email));
    return onSnapshot(
      q,
      (snap) =>
        setOnboarding(
          snap.docs
            .map((d) => d.data() as OnboardingChecklist)
            .filter((c) => c.status === 'active' && (c.openCountByEmail?.[email] ?? 0) > 0)
            .map((c) => {
              const off = c.kind === 'offboarding';
              const n = c.openCountByEmail?.[email] ?? 0;
              return {
                id: `onb:${c.id}`,
                kind: 'onboarding' as const,
                tool: 'On/Offboarding',
                subgroup: off ? 'Offboarding' : 'Onboarding',
                label: `${off ? 'Offboarding' : 'Onboarding'} — ${c.employeeName} · ${n} task${n === 1 ? '' : 's'} for you`,
                link: `/onboarding/checklists/${c.id}`,
              };
            }),
        ),
      () => setOnboarding([]),
    );
  }, [email]);

  // Project action items assigned to me that are overdue or due within 3 days.
  useEffect(() => {
    const projectsEnabled = ready && state.status === 'ready' && state.user?.tools?.projects?.enabled;
    if (!email || !projectsEnabled) {
      setProjectItems([]);
      return;
    }
    const today = new Date();
    const in3Days = new Date(today.getTime() + 3 * 86_400_000).toISOString().slice(0, 10);
    const todayStr = today.toISOString().slice(0, 10);
    const q = query(
      collection(db, COLLECTIONS.PROJECT_ACTION_ITEMS),
      where('assigneeEmail', '==', email),
      where('status', 'in', ['Open', 'Blocked']),
    );
    return onSnapshot(
      q,
      (snap) =>
        setProjectItems(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() } as ProjectActionItem & { id: string }))
            .filter((item) => item.dueDate && item.dueDate <= in3Days)
            .map((item) => {
              const overdue = item.dueDate! < todayStr;
              return {
                id: `proj:${item.id}`,
                kind: 'projects' as const,
                tool: 'Projects',
                label: `Project item ${overdue ? 'overdue' : 'due soon'}: ${item.title}`,
                link: '/projects/items',
              };
            }),
        ),
      () => setProjectItems([]),
    );
  }, [email, ready, state.status === 'ready' && state.user?.tools?.projects?.enabled]);

  // New BigQuery tables detected — surfaced only to ANALYTICS TOOL admins
  // (tools.analytics.role admin/approver_admin), cleared once acknowledged on
  // the BQ Status page (arrayUnion of my email). Deliberately NARROWER than
  // the page-access check: app admins (config.adminEmails — expense-tool
  // admins like Sophie/hr@) must NOT inherit this bell; the scan email is
  // scoped the same way server-side.
  const isAnalyticsAdminUser =
    ready && isAnalyticsAdmin({ isAppAdmin: false, role: state.user.tools?.analytics?.role });
  useEffect(() => {
    if (!email || !isAnalyticsAdminUser) {
      setBqAlerts([]);
      return;
    }
    return onSnapshot(
      collection(db, COLLECTIONS.BQ_STATUS_ALERTS),
      (snap) => {
        const fresh = snap.docs
          .map((d) => d.data() as BqStatusAlert)
          .filter((a) => (a.tables?.length ?? 0) > 0 && !(a.acknowledgedBy ?? []).includes(email));
        const n = fresh.reduce((s, a) => s + (a.tables?.length ?? 0), 0);
        setBqAlerts(
          n === 0
            ? []
            : [
                {
                  id: 'bq:new-tables',
                  kind: 'analytics' as const,
                  tool: 'Analytics',
                  label: `${n} new BigQuery table${n === 1 ? '' : 's'} to review`,
                  link: '/analytics/admin',
                },
              ],
        );
      },
      () => setBqAlerts([]),
    );
  }, [email, isAnalyticsAdminUser]);

  const directorsEnabled = ready && state.status === 'ready' && state.user?.tools?.directors?.enabled === true;
  useEffect(() => {
    if (!email || !directorsEnabled) { setDirectorInputs([]); return; }
    let alive = true;
    const load = async () => {
      try {
        const { todos } = (await callDirectorInputs({})).data;
        if (!alive) return;
        setDirectorInputs(todos.map((t) => ({
          id: `directors-input:${t.id}`,
          kind: 'directors' as const,
          tool: "Directors' Room",
          label: `Your input required: ${t.title}`,
          link: '/directors',
        })));
      } catch { if (alive) setDirectorInputs([]); }
    };
    void load();
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => { void load(); }, 5 * 60_000);
    return () => { alive = false; window.removeEventListener('focus', onFocus); window.clearInterval(timer); };
  }, [email, directorsEnabled]);

  return useMemo(
    () => [
      ...directorInputs,
      ...bqAlerts,
      ...onboarding,
      ...projectItems,
      ...reviews,
      ...nominations,
      ...timeApprovals,
      ...ownTime,
      ...feedback,
      ...receiptReq,
      ...signatures,
      ...ownExpense,
      ...ownInvoice,
      ...ownCc,
      ...absence,
      ...absenceChain,
      ...ownCert,
      ...expense,
      ...invoice,
      ...cc,
    ],
    [directorInputs, bqAlerts, onboarding, projectItems, reviews, nominations, timeApprovals, ownTime, feedback, receiptReq, signatures, ownExpense, ownInvoice, ownCc, absence, absenceChain, ownCert, expense, invoice, cc],
  );
}
